import React from 'react';
import { Plus, Trash2, GitBranch } from 'lucide-react';

// Conditional call transfer rules, stored on the agent as a JSON string in
// `transfer_rules`. Each rule routes the transfer to its own number when a
// session variable (e.g. university_name passed to the outbound call API)
// matches. Rules are checked top to bottom; the first match wins, and the
// Transfer Target Number above is used when none match.

const OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
];

const E164_PATTERN = /^\+[1-9]\d{1,14}$/;

const emptyRule = () => ({ variable: '', operator: 'equals', value: '', target_number: '' });

export const parseTransferRules = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

// Drops rows missing a variable or number so the API never stores half-filled
// rules; returns null when nothing is left so the column clears.
export const serializeTransferRules = (raw) => {
  const rules = parseTransferRules(raw)
    .map((r) => ({
      variable: (r.variable || '').trim(),
      operator: r.operator || 'equals',
      value: (r.value ?? '').toString().trim(),
      target_number: (r.target_number || '').trim(),
    }))
    .filter((r) => r.variable && r.target_number);
  return rules.length > 0 ? JSON.stringify(rules) : null;
};

const inputClass =
  'w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const TransferRulesEditor = ({ formData, setFormData }) => {
  const rules = parseTransferRules(formData.transfer_rules);

  const writeRules = (next) => {
    setFormData({ ...formData, transfer_rules: next.length > 0 ? JSON.stringify(next) : null });
  };

  const updateRule = (index, field, value) => {
    writeRules(rules.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  return (
    <div className="p-4 bg-background border border-border rounded-lg space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-primary" />
          Conditional Transfer Numbers
        </label>
        <p className="text-xs text-muted-foreground">
          Route the transfer to a different number based on the variables sent with the call, for
          example <code>university_name</code> passed to the outbound call API. Rules are checked top to
          bottom and the first match wins. Matching ignores upper and lower case. When no rule matches,
          or a variable is missing, the call goes to the Transfer Target Number above.
        </p>
      </div>

      {rules.length > 0 && (
        <div className="space-y-3">
          {rules.map((rule, index) => {
            const badNumber = rule.target_number && !E164_PATTERN.test(rule.target_number.trim());
            return (
              <div key={index} className="p-3 bg-muted/20 border border-border/50 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">
                    {index === 0 ? 'If' : 'Else if'}
                  </span>
                  <button
                    type="button"
                    onClick={() => writeRules(rules.filter((_, i) => i !== index))}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                    title="Remove rule"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={rule.variable || ''}
                    onChange={(e) => updateRule(index, 'variable', e.target.value)}
                    className={inputClass}
                    placeholder="Variable, e.g. university_name"
                  />
                  <select
                    value={rule.operator || 'equals'}
                    onChange={(e) => updateRule(index, 'operator', e.target.value)}
                    className={inputClass}
                  >
                    {OPERATORS.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={rule.value ?? ''}
                    onChange={(e) => updateRule(index, 'value', e.target.value)}
                    className={inputClass}
                    placeholder="Value, e.g. Arcadia"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Transfer to</label>
                  <input
                    type="tel"
                    value={rule.target_number || ''}
                    onChange={(e) => updateRule(index, 'target_number', e.target.value)}
                    className={inputClass}
                    placeholder="+1234567890"
                  />
                  {badNumber && (
                    <p className="text-xs text-destructive mt-1">Use E.164 format, e.g. +1234567890.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={() => writeRules([...rules, emptyRule()])}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg text-foreground hover:bg-muted/40 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add rule
      </button>
    </div>
  );
};

export default TransferRulesEditor;
